/**
 * InboxScreen v3 (Sprint 1.5)
 *
 * Changes vs Sprint 2:
 *   - Logout flow: warning "hai le 24 parole?" + clear local backup too
 *   - New button: cambia PIN
 *   - Renamed button: "SVUOTA QUESTO DISPOSITIVO" instead of LOGOUT_&_FORGET
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Frame, Header, Footer, Btn, Box, Cursor,
} from '../components/Frame';
import { ChatRow, isNewChat } from '../components/ChatRow';
import { CardLink } from '../components/Section';
import { isNativeApp } from '../lib/platform';
import { useStore } from '../lib/store';
import { auth, signOut, callFn } from '../lib/firebase';
import { clearSession } from '../lib/storage';
import { wipeKeys } from '../lib/keys';
import { clearLocalBackup, clearLastSigil, resetLocalAttempts } from '../lib/pinCrypto';
import { ensureSigilRegistered, isSelfReadable, setSelfReadable, redeemInvite, hideChat, subscribeMySignal, listMyInvites, confirmRedemption } from '../lib/messaging';
import { getMyAccount, localKeysForDerivedSigil } from '../lib/derivedSigil';
import { getCachedChatList, setCachedChatList } from '../lib/sessionCache';
import {
  enableNotifications,
  disableNotifications,
  areNotificationsEnabled,
  installForegroundMessageListener,
  isNotifSoundEnabled,
  setNotifSoundEnabled,
  playNotificationSound,
  primeNotificationAudio,
  ensureNotificationsHealthy,
} from '../lib/notifications';

export default function InboxScreen() {
  const navigate = useNavigate();
  const { sigil, uid, firebaseUser, reset, mnemonic, setPremium, setPremiumInfo, setDerivedKeys, derivedKeys,
    premiumStatus, premiumDaysLeft, premiumGraceDaysLeft, premium, isFounder, premiumLifetime } = useStore();
  const hasPremium = premium || isFounder || premiumLifetime;
  const [pendingConfirms, setPendingConfirms] = useState([]); // riscatti inviti in attesa di MIA conferma (lista)
  const [confirmBusy, setConfirmBusy] = useState(null); // token in corso di conferma/rifiuto dal banner
  const myFirebaseUid = firebaseUser?.uid;
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoutWarn, setShowLogoutWarn] = useState(false);
  const [chats, setChats] = useState(() => getCachedChatList(myFirebaseUid));
  const [error, setError] = useState(null);
  const [notifOn, setNotifOn] = useState(false);
  const [notifMsg, setNotifMsg] = useState(null);
  const [notifBusy, setNotifBusy] = useState(false);
  const [notifBannerOff, setNotifBannerOff] = useState(() => {
    try { return localStorage.getItem('nullum.notifBannerOff') === '1'; } catch { return false; }
  });
  const [selfRead, setSelfRead] = useState(isSelfReadable());
  const [soundOn, setSoundOn] = useState(() => { try { return isNotifSoundEnabled(); } catch { return true; } });
  const notifSupported = typeof window !== 'undefined' && 'Notification' in window;

  const fetchChats = useCallback(async () => {
    if (!myFirebaseUid) return;
    try {
      const fn = callFn('listMyChats');
      const res = await fn({ nullumUid: uid });
      const payload = res.data;
      if (payload?.ok) {
        const items = (payload.chats || []).map((c) => ({
          ...c,
          callerUid: myFirebaseUid,
        }));
        setChats(items);
        setCachedChatList(myFirebaseUid, items);
        setError(null);
      } else {
        console.warn('listMyChats server error:', payload?.error);
      }
    } catch (e) {
      console.warn('listMyChats failed', e);
    }
  }, [myFirebaseUid, uid]);

  // Riscatti inviti in attesa di MIA conferma (monouso "su richiesta"): lista
  // per il banner azionabile nell'inbox.
  const refreshConfirms = useCallback(() => (
    listMyInvites()
      .then((list) => setPendingConfirms((list || []).filter((i) => i.redeemConfirmStatus === 'pending')))
      .catch(() => {})
  ), []);

  // Conferma/rifiuta un riscatto DIRETTAMENTE dal banner inbox (no navigazione).
  async function doConfirmFromInbox(token, accept) {
    if (!accept && !confirm('Rifiutare?\n\nChi ha usato il link non diventerà un tuo contatto.')) return;
    setConfirmBusy(token);
    try { await confirmRedemption(token, accept); await refreshConfirms(); fetchChats(); }
    catch (e) { console.warn('confirmRedemption (inbox) failed', e); }
    finally { setConfirmBusy(null); }
  }

  // ── Dashboard: la home mostra SOLO le notifiche ("Da vedere"), non l'elenco
  // completo delle conversazioni (quello vive in /conversazioni). ──
  const allChats = (chats || [])
    .slice()
    .sort((a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0)); // più recenti in cima

  // "Da vedere": richieste in arrivo (chat pending iniziate dall'altro) +
  // chat con messaggi non letti. Sono notifiche, non una lista di chat.
  const initiatorOf = (c) => c.initiator ?? c.requestedBy ?? null;
  const isIncomingRequest = (c) => c.status === 'pending' && !!initiatorOf(c) && initiatorOf(c) !== c.callerUid;
  const needsAttention = (c) => isIncomingRequest(c) || isNewChat(c);
  const toSeeChats = allChats.filter(needsAttention);
  const reqCount = allChats.filter(isIncomingRequest).length;
  const unreadCount = allChats.filter((c) => isNewChat(c) && !isIncomingRequest(c)).length;
  const totalCount = allChats.length;
  const renderRow = (c) => (
    <ChatRow
      key={c.id}
      chat={c}
      isNew={isNewChat(c)}
      onClick={() => navigate(`/chat/${c.id}`)}
      onHide={async () => {
        try { await hideChat(c.id, 'self'); await fetchChats(); }
        catch (e) { setError(e?.message || 'rimozione fallita'); }
      }}
      onDeleteBoth={async () => {
        try { await hideChat(c.id, 'both'); await fetchChats(); }
        catch (e) { setError(e?.message || 'cancellazione fallita'); }
      }}
    />
  );

  // REAL-TIME al posto del polling: un listener sul proprio doc utente
  // (users/{uid}.rtSignalAt) sveglia la fetch SOLO quando c'è attività vera.
  // Utente fermo = ~0 letture. Resta un poll lento (30s) come rete di sicurezza
  // nel caso un segnale si perda. Sostituisce il vecchio poll a 4s.
  useEffect(() => {
    if (!myFirebaseUid) return;
    let alive = true;
    fetchChats(); refreshConfirms(); // primo caricamento
    let lastTick = 0;
    const off = subscribeMySignal(myFirebaseUid, (millis) => {
      if (!alive) return;
      if (millis && millis === lastTick) return; // dedup
      lastTick = millis;
      fetchChats(); refreshConfirms();
    });
    const slow = setInterval(() => { if (alive) fetchChats(); }, 30000);
    return () => { alive = false; try { off && off(); } catch {} clearInterval(slow); };
  }, [myFirebaseUid, fetchChats, refreshConfirms]);

  // Auto-recupero: assicura che la mappa sigil→uid esista sul server.
  // Se manca (es. dopo un wipe), la ricrea -> l'utente torna trovabile dagli
  // altri senza fare nulla. Idempotente; una sola volta per sessione.
  const sigilCheckedRef = useRef(false);
  useEffect(() => {
    if (!myFirebaseUid || !sigil || sigilCheckedRef.current) return;
    sigilCheckedRef.current = true;
    ensureSigilRegistered(sigil, myFirebaseUid).then(async (r) => {
      if (r === 'takeover') {
        console.info(`sigil @${sigil}: questo dispositivo è ora l'attivo`);
        fetchChats();
        return;
      }
      if (r !== 'restored') return;
      console.info(`sigil @${sigil} ri-registrato (mappa mancante sul server)`);
      // NOTA: l'auto-restore del backup remoto dal blob locale e' stato
      // rimosso. Ora il blob non e' piu' in localStorage (per evitare brute
      // force offline). Se il backup remoto viene perso (es. wipe admin),
      // il recovery passa esclusivamente per le 24 parole.
      fetchChats();
    });
  }, [myFirebaseUid, sigil, fetchChats]);

  // Invito in sospeso: dopo onboarding/sblocco, porta l'utente a scrivere
  // all'emittente. Due percorsi:
  //  (a) NUOVO: InviteScreen ha gia' fatto il redeem al click e salvato
  //      connectToFull/connectTo → basta navigare a /new (NewChatScreen lo usa).
  //  (b) LEGACY: solo il token in pendingInvite → redeem qui, poi /new.
  const inviteCheckedRef = useRef(false);
  useEffect(() => {
    if (!myFirebaseUid || inviteCheckedRef.current) return;

    // (a) connessione gia' pronta da InviteScreen
    let hasConnect = false;
    try { hasConnect = !!(sessionStorage.getItem('nullum.connectToFull') || sessionStorage.getItem('nullum.connectTo')); } catch {}
    if (hasConnect) {
      inviteCheckedRef.current = true;
      navigate('/new');
      return;
    }

    // (b) token legacy da riscattare
    let token;
    try { token = sessionStorage.getItem('nullum.pendingInvite'); } catch {}
    if (!token) return;
    inviteCheckedRef.current = true;
    // Passcode eventuale (invito "a distanza"), stashed dall'InviteScreen.
    let pendPass = null;
    try { pendPass = sessionStorage.getItem('nullum.pendingInvitePass'); } catch {}
    try { sessionStorage.removeItem('nullum.pendingInvite'); } catch {}
    try { sessionStorage.removeItem('nullum.pendingInvitePass'); } catch {}
    // Redeem come IDENTITÀ corrente (Opzione 2): invited + whitelist sull'uid reale.
    redeemInvite(token, pendPass || null).then((r) => {
      if (r?.ok && r.creator?.sigil) {
        try { sessionStorage.setItem('nullum.connectTo', r.creator.sigil); } catch {}
        try { sessionStorage.setItem('nullum.connectToFull', JSON.stringify(r.creator)); } catch {}
        navigate('/new');
      }
    }).catch((e) => console.warn('redeem invito in sospeso fallito', e?.message || e));
  }, [myFirebaseUid, navigate]);

  // Carica stato premium + chiavi derivate dal mnemonic (per decifrare chat
  // ricevute su sigil derivati). One-shot al primo mount con mnemonica.
  const accountLoadedRef = useRef(false);
  useEffect(() => {
    if (!myFirebaseUid || !mnemonic || accountLoadedRef.current) return;
    accountLoadedRef.current = true;
    (async () => {
      try {
        const r = await getMyAccount();
        setPremiumInfo?.(r);
        const map = {};
        for (const d of (r.derivedSigils || [])) {
          try {
            const keys = await localKeysForDerivedSigil({ mnemonic, sigilLower: d.sigilLower });
            map[d.sigilLower] = { ...keys, sigil: d.sigil };
          } catch (e) {
            console.warn('derived key derivation failed for', d.sigilLower, e);
          }
        }
        setDerivedKeys?.(map);
      } catch (e) { console.warn('getMyAccount failed', e); }
    })();
  }, [myFirebaseUid, mnemonic, setPremiumInfo, setDerivedKeys]);

  // Push: stato iniziale + listener foreground (app aperta → refresh chat).
  useEffect(() => {
    try { setNotifOn(areNotificationsEnabled()); } catch {}
    const off = installForegroundMessageListener(() => { fetchChats(); });
    // Self-healing push: se le avevi attive, le ri-registro dopo update/login.
    ensureNotificationsHealthy().then((r) => {
      if (r?.ok) { try { setNotifOn(true); } catch {} }
    }).catch(() => {});
    // Sblocca l'audio al primo tap/touch (i browser lo richiedono).
    const prime = () => { try { primeNotificationAudio(); } catch {} };
    window.addEventListener('pointerdown', prime, { once: true });
    return () => { off?.(); window.removeEventListener('pointerdown', prime); };
  }, [fetchChats]);

  // Diagnostica: banner in-page quando una FCM push arriva mentre l'app
  // e' in foreground. Cosi' sappiamo se il problema e' "non arriva proprio"
  // (banner mai compare) o "arriva ma OS non la mostra" (banner compare ma
  // niente nel pannello notifiche).
  const [fcmInPageHit, setFcmInPageHit] = useState(null);
  useEffect(() => {
    function onHit(e) {
      const title = e?.detail?.notification?.title || 'FCM';
      setFcmInPageHit(`✓ push ricevuta in foreground: ${title}`);
      setTimeout(() => setFcmInPageHit(null), 6000);
    }
    window.addEventListener('nullum:fcm-foreground', onHit);
    return () => window.removeEventListener('nullum:fcm-foreground', onHit);
  }, []);

  async function doTestPush() {
    setNotifMsg(null);
    setNotifBusy(true);
    try {
      let res = await callFn('sendTestPush')({});
      let d = res?.data || {};

      // Auto-recovery: se il server dice che il token e' scaduto o non
      // esistono device, rinnoviamo (disable -> enable -> retry).
      const allStale = d.ok && d.sent === 0 && (d.failures || []).every((f) =>
        f.code === 'messaging/registration-token-not-registered' ||
        f.code === 'messaging/invalid-registration-token'
      );
      if (allStale) {
        setNotifMsg('token scaduto · rinnovo in corso...');
        try { await disableNotifications(); } catch {}
        const r = await enableNotifications();
        if (!r.ok) {
          setNotifMsg(`rinnovo fallito: ${r.reason || 'errore'}${r.error ? ' · ' + r.error : ''}`);
          return;
        }
        setNotifOn(true);
        // Retry una volta.
        res = await callFn('sendTestPush')({});
        d = res?.data || {};
      }

      if (d.ok && d.sent > 0) {
        setNotifMsg(`push test inviata a ${d.sent} device · attendi la notifica`);
      } else if (d.ok && d.sent === 0) {
        const codes = (d.failures || []).map((f) => f.code).join(', ') || 'nessun device registrato';
        setNotifMsg(`nessuna push consegnata (${codes})`);
      } else {
        setNotifMsg('errore: risposta inattesa');
      }
    } catch (e) {
      const code = e?.code || '';
      const msg = e?.message || '';
      const text = `${code} ${msg} ${String(e)}`;
      // NO_DEVICES_REGISTERED: il server non ha piu' nessun device per noi
      // (token rimossi dal cleanup precedente). Riattiviamo silenziosamente.
      // NB: con HttpsError, code='functions/failed-precondition' e il payload
      // sta nel message -> controlliamo entrambi.
      if (text.includes('NO_DEVICES_REGISTERED') || text.includes('NO_TOKENS') || code === 'functions/failed-precondition') {
        setNotifMsg('nessun device sul server · riattivo notifiche...');
        try {
          const r = await enableNotifications();
          if (r.ok) {
            setNotifOn(true);
            const res2 = await callFn('sendTestPush')({});
            const d2 = res2?.data || {};
            if (d2.ok && d2.sent > 0) {
              setNotifMsg(`push test inviata a ${d2.sent} device · attendi la notifica`);
              return;
            }
            const codes2 = (d2.failures || []).map((f) => `${f.code || '?'}: ${f.message || ''}`).join(' | ') || 'nessun device';
            setNotifMsg(`dopo rinnovo: sent=${d2?.sent || 0} failed=${d2?.failed || 0}. Codici FCM: ${codes2}`);
            return;
          }
          setNotifMsg(`riattivazione fallita: ${r.reason || 'errore'}${r.error ? ' · ' + r.error : ''}`);
          return;
        } catch (e2) {
          setNotifMsg(`riattivazione fallita: ${e2?.message || e2}`);
          return;
        }
      }
      setNotifMsg(`errore: ${code}${msg ? ' · ' + msg : ''}`);
    } finally {
      setNotifBusy(false);
    }
  }

  async function toggleNotifications() {
    setNotifMsg(null);
    setNotifBusy(true);
    try {
      if (notifOn) {
        await disableNotifications();
        setNotifOn(false);
        setNotifMsg('notifiche disattivate');
        return;
      }
      const r = await enableNotifications();
      if (r.ok) {
        setNotifOn(true);
        setNotifMsg('notifiche attivate ✓');
      } else {
        // Istruzioni "come riabilitare" specifiche per piattaforma.
        const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
        const isIOS = /iPad|iPhone|iPod/.test(ua) ||
          (ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document);
        const isAndroid = /Android/i.test(ua);
        const howToReEnable = isIOS
          ? 'iPhone: Impostazioni → Notifiche → Nullum → Consenti notifiche.'
          : isAndroid
          ? 'Android Chrome: tocca il lucchetto a sinistra dell\'URL → Autorizzazioni → Notifiche → Consenti. (oppure ⋮ → Impostazioni → Impostazioni sito → Notifiche → trova nullum.app)'
          : 'Desktop: click sul lucchetto nella barra indirizzi → Notifiche → Consenti, poi ricarica la pagina.';

        const reasons = {
          IOS_NEEDS_INSTALL:
            "su iPhone le notifiche funzionano SOLO se hai prima installato Nullum sulla home. Apri menu → ⚙ Impostazioni → 'Come installare sul telefono', aggiungi l'icona alla home, poi apri Nullum DALLA ICONA (non da Safari) e riprova qui.",
          PERMISSION_DENIED:
            `permesso negato. Devi riabilitarlo a mano nelle impostazioni del browser/telefono. ${howToReEnable}`,
          PERMISSION_DISMISSED:
            'hai chiuso la richiesta senza decidere. Riprova e tocca "Consenti".',
          NOT_SUPPORTED: 'questo browser non supporta le notifiche.',
          PURE_SESSION_INCOMPATIBLE: 'non disponibili in modalità pure session.',
          NO_TOKEN: `token non ottenuto. Probabile permesso non davvero concesso, o service worker bloccato. ${howToReEnable}`,
        };
        const base = reasons[r.reason] || `non attivate (${r.reason || 'errore'})`;
        setNotifMsg(r.error ? `${base} · dettaglio: ${r.error}` : base);
      }
    } finally {
      setNotifBusy(false);
    }
  }

  const [updateMsg, setUpdateMsg] = useState(null);
  const [updateBusy, setUpdateBusy] = useState(false);

  // Confronta la versione che sta girando (__APP_VERSION__ del bundle attuale)
  // con quella deployata sul server (estratta dal bundle live). Se diverse,
  // pulisce tutto e ricarica. Se uguali, conferma "gia' aggiornata".
  async function checkForUpdates() {
    setUpdateMsg(null);
    setUpdateBusy(true);
    try {
      const running = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '?';
      // Scarica index.html con cache-buster per leggere l'asset JS principale.
      const htmlRes = await fetch(`/?_=${Date.now()}`, { cache: 'no-store' });
      const html = await htmlRes.text();
      const m = html.match(/src="(\/assets\/index-[^"]+\.js)"/);
      let remote = '?';
      if (m) {
        const jsRes = await fetch(`${m[1]}?_=${Date.now()}`, { cache: 'no-store' });
        const js = await jsRes.text();
        const v = js.match(/"(2[0-9]{3}-[0-9]{2}-[0-9]{2} [0-9]{2}:[0-9]{2})"/);
        if (v) remote = v[1];
      }
      if (remote === '?' || running === remote) {
        setUpdateMsg(`gia' aggiornata (v${running})`);
        setUpdateBusy(false);
        return;
      }
      setUpdateMsg(`nuova versione disponibile: v${remote} (tu: v${running}). aggiorno...`);
      // Pulizia totale + reload.
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
      if (typeof caches !== 'undefined' && caches?.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
      }
      const url = new URL(window.location.href);
      url.searchParams.set('_', String(Date.now()));
      window.location.replace(url.toString());
    } catch (e) {
      console.warn('checkForUpdates error', e);
      setUpdateMsg(`errore: ${e?.message || e}`);
      setUpdateBusy(false);
    }
  }

  async function doLogout() {
    try { await signOut(auth()); } catch {}
    try { clearSession(); } catch {}
    try { await wipeKeys(); } catch {}
    try { clearLocalBackup(); } catch {}
    try { clearLastSigil(); } catch {}
    try { resetLocalAttempts(); } catch {}
    reset();
    navigate('/welcome', { replace: true });
  }

  // "Esci": blocca la sessione (azzera lo stato in memoria con un reload), MA
  // NON cancella il backup → si rientra col PIN. Diverso da "Svuota dispositivo".
  function doExit() {
    try { reset(); } catch {}
    window.location.assign('/');
  }

  if (!sigil) {
    return (
      <Frame statusLeft="INBOX :: NO_SESSION" statusRight="5G">
        <Header title="// INBOX" />
        <Box variant="warn">
          <p className="text-xs">Sessione non trovata.</p>
        </Box>
        <Footer>
          <Btn onClick={() => navigate('/')}>// back</Btn>
        </Footer>
      </Frame>
    );
  }

  return (
    <Frame statusLeft="INBOX" statusRight="5G">
      <Header
        title={`// @${sigil}`}
        crumb={menuOpen ? '✕ chiudi' : '☰ menu'}
        onCrumbClick={() => setMenuOpen((v) => !v)}
      />

      {menuOpen && (
        <div className="mb-3">
          <CardLink
            icon="⭐" title="Premium" subtitle="abbonamento e funzioni in più"
            onClick={() => { setMenuOpen(false); navigate('/premium'); }}
          />
          <CardLink
            icon="⚙" title="Impostazioni" subtitle="notifiche, PIN, privacy, app"
            onClick={() => { setMenuOpen(false); navigate('/impostazioni'); }}
          />
          <CardLink
            icon="ℹ" title="Info & sicurezza" subtitle="guida, sicurezza, termini"
            onClick={() => { setMenuOpen(false); navigate('/info-hub'); }}
          />
          {isNativeApp() && (
            <CardLink
              icon="🧅" title="IP anonimo (Tor)" subtitle="nascondi da dove ti colleghi"
              onClick={() => { setMenuOpen(false); navigate('/tor'); }}
            />
          )}
          <CardLink
            icon="🚪" title="Esci" subtitle="blocca la sessione · rientri col PIN"
            onClick={() => {
              setMenuOpen(false);
              if (confirm('Uscire da Nullum?\n\nPer rientrare ti servirà il PIN. Il tuo account resta.')) doExit();
            }}
          />
        </div>
      )}

      {showLogoutWarn && (
        <Box variant="danger" className="mb-3 text-[16px] space-y-2">
          <p className="text-red-500">// SVUOTA QUESTO DISPOSITIVO</p>
          <p className="text-ink-dim">
            Cancella tutto da questo dispositivo: chiavi e sigil.
          </p>
          <p className="text-ink-dim">
            <span className="text-amber">!</span> Hai salvato le 24 parole?
            Con quelle rientri anche se dimentichi il PIN.
            Altrimenti rientri con <b>@sigil + PIN</b> da qualsiasi dispositivo.
          </p>
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={() => setShowLogoutWarn(false)}>
              ◀ annulla
            </Btn>
            <Btn variant="danger" onClick={doLogout}>
              🗑 svuota
            </Btn>
          </div>
        </Box>
      )}

      {error && (
        <Box variant="danger" className="text-[16px] mb-3">
          errore: {error}
        </Box>
      )}

      {fcmInPageHit && (
        <Box variant="success" className="mb-3 text-[14px]">
          <p className="text-ink">{fcmInPageHit}</p>
          <p className="text-[12px] text-ink-dim mt-1">
            Se non compare anche la notifica di sistema, controlla
            Impostazioni Chrome → Notifiche del sito.
          </p>
        </Box>
      )}

      {/* Riscatti inviti da confermare: chi ha usato un tuo invito monouso non
          diventa contatto finché non lo confermi qui. */}
      {pendingConfirms.length > 0 && (
        <Box variant="warn" className="mb-3 text-[14px] space-y-2">
          <p className="text-amber">
            🔔 {pendingConfirms.length === 1
              ? 'Una persona ha usato un tuo invito'
              : `${pendingConfirms.length} persone hanno usato i tuoi inviti`}: conferma chi è prima che diventi tuo contatto.
          </p>
          {pendingConfirms.map((iv) => {
            const known = !!iv.pendingRedeemerSigil;
            return (
              <div key={iv.token} className="border border-ink-dim/40 rounded-lg p-2">
                <div className="text-[13px]">
                  {known
                    ? <span className="text-ink">ha riscattato: <b>@{iv.pendingRedeemerSigil}</b></span>
                    : <span className="text-ink-dim">qualcuno ha aperto il link ma non è entrato — puoi rifiutarlo</span>}
                </div>
                <div className="flex gap-2 mt-1.5">
                  {known && (
                    <button
                      onClick={() => doConfirmFromInbox(iv.token, true)}
                      disabled={confirmBusy === iv.token}
                      className="border border-ink rounded-lg bg-ink/10 text-ink text-glow text-[12px] px-3 py-1 hover:bg-ink/20 transition-colors disabled:opacity-40"
                    >
                      {confirmBusy === iv.token ? '…' : '✓ Conferma'}
                    </button>
                  )}
                  <button
                    onClick={() => doConfirmFromInbox(iv.token, false)}
                    disabled={confirmBusy === iv.token}
                    className="border border-redphos/50 rounded-lg text-redphos text-[12px] px-3 py-1 hover:bg-redphos/10 transition-colors disabled:opacity-40"
                  >
                    {confirmBusy === iv.token ? '…' : '✕ Rifiuta'}
                  </button>
                </div>
              </div>
            );
          })}
        </Box>
      )}

      {/* Avviso scadenza abbonamento: compare negli ultimi 7 giorni (7/3/1) e
          durante la grazia. Sparisce da solo al rinnovo. Non dismissibile:
          è temporaneo e serve a non far perdere dati al cliente. */}
      {premiumStatus === 'active' && premiumDaysLeft != null && premiumDaysLeft <= 7 && (
        <Box variant="warn" className="mb-3 text-[14px]">
          <p className={premiumDaysLeft <= 1 ? 'text-red-400' : 'text-amber'}>
            ⏳ Il tuo Premium scade {premiumDaysLeft <= 1 ? 'entro 24 ore' : `tra ${premiumDaysLeft} giorni`}.
            {' '}Rinnova per non perdere sigil derivati e chat a lunga durata.
          </p>
          <Btn variant="primary" className="mt-2" onClick={() => navigate('/premium')}>Rinnova Premium</Btn>
        </Box>
      )}
      {premiumStatus === 'grace' && (
        <Box variant="danger" className="mb-3 text-[14px]">
          <p className="text-red-400">
            ⚠ Premium scaduto — periodo di grazia{premiumGraceDaysLeft != null ? ` (${premiumGraceDaysLeft} giorni rimasti)` : ''}.
            {' '}Ora puoi solo leggere e ricevere. Alla fine della grazia perdi
            sigil derivati e chat a lunga durata.
          </p>
          <Btn variant="primary" className="mt-2" onClick={() => navigate('/premium')}>Rinnova ora</Btn>
        </Box>
      )}

      {notifSupported && !notifOn && !notifBannerOff && (
        <Box variant="warn" className="mb-3 text-[14px]">
          <div className="flex items-start justify-between gap-2">
            <p className="text-amber">🔔 Attiva le notifiche: saprai subito quando arriva un messaggio.</p>
            <button
              aria-label="chiudi"
              onClick={() => { try { localStorage.setItem('nullum.notifBannerOff', '1'); } catch {} setNotifBannerOff(true); }}
              className="text-ink-dim hover:text-ink text-[18px] leading-none px-1"
            >✕</button>
          </div>
          <Btn variant="ghost" className="mt-2" onClick={toggleNotifications} disabled={notifBusy}>
            {notifBusy ? '…' : 'Attiva notifiche'}
          </Btn>
          {notifMsg && <p className="text-[12px] text-ink-dim mt-1 break-words select-text">{notifMsg}</p>}
        </Box>
      )}

      {/* Campo per scrivere un messaggio (porta alla compose). */}
      <button
        onClick={() => navigate('/new')}
        aria-label="Nuovo messaggio"
        className="w-full flex items-center gap-2 rounded-xl border border-ink-dim/60 bg-ink/[0.02] text-ink-dim hover:border-ink hover:text-ink px-3 py-3 mb-3 text-left transition-colors touch-manipulation"
      >
        <span className="text-[18px] leading-none">✍️</span>
        <span className="text-[15px]">Scrivi un messaggio…</span>
      </button>

      {/* Azioni secondarie. */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          onClick={() => navigate('/whitelist')}
          aria-label="Rubrica contatti"
          className="flex items-center justify-center gap-2 rounded-xl border border-ink-dim/60 text-ink-dim hover:text-ink hover:border-ink py-2.5 transition-colors touch-manipulation"
        >
          <span className="text-[16px] leading-none">👤</span><span className="text-[13px]">Rubrica</span>
        </button>
        <button
          onClick={() => navigate('/invite')}
          aria-label="Crea un invito"
          className="flex items-center justify-center gap-2 rounded-xl border border-ink-dim/60 text-ink-dim hover:text-ink hover:border-ink py-2.5 transition-colors touch-manipulation"
        >
          <span className="text-[16px] leading-none">🔗</span><span className="text-[13px]">Invita</span>
        </button>
      </div>

      {/* Massima sicurezza (TUONO) — accesso diretto in home per i premium. */}
      {hasPremium && (
        <button
          onClick={() => navigate('/tuono')}
          aria-label="Massima sicurezza (TUONO)"
          className="w-full flex items-center justify-center gap-2 rounded-xl border border-amber text-amber hover:bg-amber hover:text-black py-2.5 mb-3 transition-colors touch-manipulation"
        >
          <span className="text-[16px] leading-none">⚡</span><span className="text-[14px]">Massima sicurezza</span>
        </button>
      )}

      {/* Conversazioni anonime (sealed): il server non sa chi scrive. Per tutti. */}
      <button
        onClick={() => navigate('/sealed')}
        aria-label="Conversazioni anonime"
        className="w-full flex items-center justify-center gap-2 rounded-xl border border-amber/60 text-amber hover:bg-amber/10 py-2.5 mb-3 transition-colors touch-manipulation"
      >
        <span className="text-[16px] leading-none">🛡</span><span className="text-[14px]">Anonimo · nascondi chi scrive</span>
      </button>

      {/* Accesso all'elenco COMPLETO delle conversazioni — vive FUORI dalla home,
          così la home resta una dashboard (notifiche + scrivi), non una lista. */}
      {totalCount > 0 && (
        <button
          onClick={() => navigate('/conversazioni')}
          aria-label="Tutte le conversazioni"
          className="w-full flex items-center justify-between gap-2 rounded-xl border border-ink-dim/60 text-ink-dim hover:text-ink hover:border-ink px-3 py-2.5 mb-4 transition-colors touch-manipulation"
        >
          <span className="flex items-center gap-2 min-w-0">
            <span className="text-[16px] leading-none">💬</span>
            <span className="text-[14px]">Conversazioni</span>
            <span className="text-[12px] text-ink-faint">({totalCount})</span>
          </span>
          <span className="flex items-center gap-2 shrink-0">
            {unreadCount > 0 && (
              <span className="text-[11px] text-amber border border-amber rounded-full px-2 py-0.5">
                {unreadCount} non lett{unreadCount === 1 ? 'o' : 'i'}
              </span>
            )}
            <span className="text-[16px] leading-none">›</span>
          </span>
        </button>
      )}

      {/* HOME = solo notifiche ("Da vedere"). L'elenco completo è in /conversazioni. */}
      <div className="flex-1 min-h-[200px]">
        {chats === null ? (
          <div className="text-center text-ink-dim text-[16px] py-8">
            loading<Cursor />
          </div>
        ) : toSeeChats.length > 0 ? (
          <>
            <div className="flex items-center flex-wrap gap-x-2 text-[12px] mb-2 mt-1">
              <span className="text-amber">🔔 Da vedere</span>
              <span className="text-ink-dim">
                {[
                  reqCount > 0 ? `${reqCount} in arrivo` : null,
                  unreadCount > 0 ? `${unreadCount} non lett${unreadCount === 1 ? 'o' : 'i'}` : null,
                ].filter(Boolean).join(' · ')}
              </span>
            </div>
            {toSeeChats.map(renderRow)}
          </>
        ) : chats.length === 0 ? (
          <div className="text-center text-ink-dim text-[16px] py-10">
            <div className="text-[40px] mb-3 opacity-60">💬</div>
            <p className="text-ink text-glow">Nessuna conversazione</p>
            <p className="text-ink-faint mt-2">
              tocca &laquo;Scrivi un messaggio&raquo; qui sopra e usa il sigil di chi vuoi contattare.
            </p>
          </div>
        ) : (
          <div className="text-center text-ink-dim text-[14px] py-10">
            <div className="text-[32px] mb-2 opacity-50">✓</div>
            <p className="text-ink">Nessuna novità</p>
            <p className="text-ink-faint mt-1">
              trovi le tue chat in &laquo;Conversazioni&raquo; qui sopra.
            </p>
          </div>
        )}
      </div>

    </Frame>
  );
}
